import {
	Alert,
	Badge,
	Box,
	Button,
	Icons,
	Inline,
	RelativeTime,
	Spinner,
	Stack,
	Text,
} from "@sembark-travel/ui-base"
import { useXHR } from "@sembark-travel/xhr"
import { useState } from "react"
import useSWR from "swr"
import { WhatsAppContactAvatar } from "./conversation/ContactAvatar"
import { fetchWhatsAppInboxThreads, TWhatsAppThread } from "./store"
import { WhatsAppInboxThread } from "./Thread"

/**
 * The conversations linked to one record, for a quick chat without leaving
 * the page — a lead's row, say. One conversation opens straight away; more
 * than one shows a picker first, with a way back to it.
 *
 * Fills its container, so the caller gives it a height.
 */
export function WhatsAppLinkedConversations({
	modelType,
	modelId,
	emptyStateMessage = "No WhatsApp conversations are linked yet.",
}: {
	// The record's morph key and id — the pair the link endpoint takes.
	modelType: string
	modelId: number
	emptyStateMessage?: string
}) {
	const xhr = useXHR()
	const { data, error } = useSWR(
		`/wa-threads?model_type=${modelType}&model_id=${modelId}`,
		() =>
			fetchWhatsAppInboxThreads(xhr, {
				model_type: modelType,
				model_id: modelId,
				limit: 50,
			})
	)
	const threads = data?.data || []
	const [selectedThreadId, setSelectedThreadId] = useState<number | null>(null)
	// With a single conversation there is nothing to choose between.
	const activeThreadId =
		selectedThreadId ?? (threads.length === 1 ? threads[0].id : null)
	const activeThread = threads.find((t) => t.id === activeThreadId)

	if (!data && !error) {
		return <Spinner alignCenter padding="4" />
	}

	if (error) {
		return (
			<Alert status="error">
				{(error as Error).message || "Could not load the conversations."}
			</Alert>
		)
	}

	if (!threads.length) {
		return (
			<Box padding="4">
				<Text color="muted">{emptyStateMessage}</Text>
			</Box>
		)
	}

	if (activeThreadId) {
		return (
			<Stack height="full" style={{ minHeight: 0 }}>
				{threads.length > 1 ? (
					<Inline
						paddingX="3"
						paddingY="2"
						borderBottomWidth="1"
						bgColor="subtle"
						alignItems="center"
						gap="2"
					>
						<Button
							level="tertiary"
							inline
							onClick={() => setSelectedThreadId(null)}
						>
							<Icons.ArrowLeft /> All conversations
						</Button>
						{activeThread ? (
							<Text fontWeight="semibold" fontSize="sm">
								{activeThread.contact?.name ||
									activeThread.contact?.contact_number ||
									"Customer"}
							</Text>
						) : null}
					</Inline>
				) : null}
				<Box flex="1" style={{ minHeight: 0 }}>
					<WhatsAppInboxThread
						key={activeThreadId}
						threadId={activeThreadId}
						trackingSurface={
							modelType === "trip_plan_requests"
								? "trip_plan_request"
								: modelType === "trips"
									? "trip"
									: "inbox"
						}
					/>
				</Box>
			</Stack>
		)
	}

	return (
		<Stack>
			{threads.map((thread) => (
				<LinkedConversationRow
					key={thread.id}
					thread={thread}
					onOpen={() => setSelectedThreadId(thread.id)}
				/>
			))}
		</Stack>
	)
}

function LinkedConversationRow({
	thread,
	onOpen,
}: {
	thread: TWhatsAppThread
	onOpen: () => void
}) {
	const preview = thread.latest_message?.message?.replace(/\s+/g, " ").trim()
	const businessNumber = thread.phone_number?.display_phone_number

	return (
		<Box
			as="button"
			type="button"
			onClick={onOpen}
			paddingX="3"
			paddingY="3"
			borderBottomWidth="1"
			textAlign="left"
			width="full"
			bgColor="default"
			style={{ cursor: "pointer" }}
		>
			<Inline gap="3" alignItems="start">
				<WhatsAppContactAvatar
					name={thread.contact?.name}
					number={thread.contact?.contact_number}
				/>
				<Stack gap="1" style={{ minWidth: 0, flex: 1 }}>
					<Inline gap="2" alignItems="baseline" justifyContent="between">
						<Text fontWeight="semibold" fontSize="sm">
							{thread.contact?.name ||
								thread.contact?.contact_number ||
								"Customer"}
						</Text>
						{thread.latest_message_at ? (
							<RelativeTime
								fontSize="xs"
								color="muted"
								timestamp={thread.latest_message_at}
							/>
						) : null}
					</Inline>
					<Inline gap="2" alignItems="center" justifyContent="between">
						<Text
							fontSize="sm"
							color="muted"
							style={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								minWidth: 0,
							}}
						>
							{preview || "No messages yet"}
						</Text>
						{thread.unread_count ? (
							<Badge primary fullRounded>
								{thread.unread_count}
							</Badge>
						) : null}
					</Inline>
					{businessNumber ? (
						<Text fontSize="xs" color="muted">
							via {businessNumber}
						</Text>
					) : null}
				</Stack>
			</Inline>
		</Box>
	)
}
